import { RouterReducerState } from '@ngrx/router-store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RouterStateUrl } from '@sd-portal/shared/util';
import { RefTypeItem } from '@sd-portal/shared/ref-type-item';
import { auditLogSelectors, refTypeItemSelectors } from '../shared-audit.adapters';
import { State } from '..';
import * as fromPostedFlight from './posted-flight.reducer';

// feature selectors
const getSharedAuditState = createFeatureSelector<State>('sharedAudit');
const getRouterState = createFeatureSelector<RouterReducerState<RouterStateUrl>>('router');

// router param selector
export const getPostedFlightId = createSelector(getRouterState, state => +state.state.params['id']);

// feature slice - posted flight
const getPostedFlightSubState = createSelector(getSharedAuditState, state => state.postedFlight);

// posted flight slices
const getApproachTypesState = createSelector(getPostedFlightSubState, state => state.approachTypes);
const getExpenseCategoryTypeState = createSelector(
  getPostedFlightSubState,
  state => state.expenseCategoryTypes
);
const getPostedFlightSkeletonState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightSkeleton
);
const getPostedFlightAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightAudit
);
const getFlightCrewMemberAuditState = createSelector(
  getPostedFlightSubState,
  state => state.flightCrewMemberAudit
);
const getFlightBusinessPersonAuditState = createSelector(
  getPostedFlightSubState,
  state => state.flightBusinessPersonAudit
);
const getLogbookApproachAuditState = createSelector(
  getPostedFlightSubState,
  state => state.logbookApproachAudit
);
const getFlightPassengerAuditState = createSelector(
  getPostedFlightSubState,
  state => state.flightPassengerAudit
);
const getExpenseAuditState = createSelector(getPostedFlightSubState, state => state.expenseAudit);
const getPostedFlightEngineAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightEngineAudit
);
const getPostedFlightComponentAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightComponentAudit
);
const getPostedFlightFuelAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightFuelAudit
);
const getPostedFlightAdditionalDetailAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightAdditionalDetailAudit
);
const getOilFluidAuditState = createSelector(getPostedFlightSubState, state => state.oilFluidAudit);
const getEngineOilFluidAuditState = createSelector(
  getPostedFlightSubState,
  state => state.engineOilFluidAudit
);
const getComponentOilFluidAuditState = createSelector(
  getPostedFlightSubState,
  state => state.componentOilFluidAudit
);
const getPostedFlightDeAntiIceAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightDeAntiIceAudit
);
const getPostedFlightNotesAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightNotesAudit
);
const getFuelUpliftAuditState = createSelector(
  getPostedFlightSubState,
  state => state.fuelUpliftAudit
);
const getBusinessCategoryAuditState = createSelector(
  getPostedFlightSubState,
  state => state.businessCategoryAudit
);
const getDepartmentAuditState = createSelector(
  getPostedFlightSubState,
  state => state.departmentAudit
);
const getLogbookAuditState = createSelector(getPostedFlightSubState, state => state.logbookAudit);
const getCrewMemberDutyAuditState = createSelector(
  getPostedFlightSubState,
  state => state.crewMemberDutyAudit
);
const getAirFrameLandingsAuditState = createSelector(
  getPostedFlightSubState,
  state => state.airframeLandingsAudit
);
const getEngineCyclesAuditState = createSelector(
  getPostedFlightSubState,
  state => state.engineCyclesAudit
);
const getVORCheckDataAuditState = createSelector(
  getPostedFlightSubState,
  state => state.vorcheckDataAudit
);
const getPreFlightChecklistUserESignaturesAuditState = createSelector(
  getPostedFlightSubState,
  state => state.preFlightChecklistUserESignaturesAudit
);
const getPostedFlightChecklistUserESignaturesAuditState = createSelector(
  getPostedFlightSubState,
  state => state.postedFlightChecklistUserESignaturesAudit
);
const getFlightLogUserESignaturesAuditState = createSelector(
  getPostedFlightSubState,
  state => state.flightLogUserESignaturesAudit
);
const getSquawkUserESignaturesAuditState = createSelector(
  getPostedFlightSubState,
  state => state.squawkUserESignaturesAudit
);

// approach types
const getApproachTypes = createSelector(getApproachTypesState, refTypeItemSelectors.selectAll);
export const getApproachTypeName = (approachTypeId: number) =>
  createSelector(getApproachTypes, (approachTypes: RefTypeItem[]) =>
    approachTypes.find(at => at.id === approachTypeId)
  );

// expense category types
const getExpenseCategoryTypes = createSelector(
  getExpenseCategoryTypeState,
  fromPostedFlight.expenseCategoryTypeSelectors.selectAll
);
export const getExpenseCategoryTypeName = (expenseCategoryTypeId: number) =>
  createSelector(getExpenseCategoryTypes, (expenseCategoryTypes: RefTypeItem[]) =>
    expenseCategoryTypes.find(ect => ect.id === expenseCategoryTypeId)
  );

// skeletons
export const getPostedFlightSkeletonLoading = createSelector(
  getPostedFlightSkeletonState,
  state => state.loading
);
export const getPostedFlightSkeletonLoaded = createSelector(
  getPostedFlightSkeletonState,
  state => state.loaded
);
export const getPostedFlightSkeleton = createSelector(
  getPostedFlightSkeletonState,
  getPostedFlightSkeletonLoaded,
  getPostedFlightId,
  (state, loaded, postedFlightId) => (loaded ? state.entities[postedFlightId] : null)
);

// posted flight audit
export const getPostedFlightAuditLoading = createSelector(
  getPostedFlightAuditState,
  state => state.loading
);
export const getPostedFlightAuditLoaded = createSelector(
  getPostedFlightAuditState,
  state => state.loaded
);
export const getPostedFlightAudit = createSelector(
  getPostedFlightAuditState,
  auditLogSelectors.selectAll
);

// flight crew member audit
export const getFlightCrewMemberAuditLoading = createSelector(
  getFlightCrewMemberAuditState,
  state => state.loading
);
export const getFlightCrewMemberAuditLoaded = createSelector(
  getFlightCrewMemberAuditState,
  state => state.loaded
);
export const getFlightCrewMemberAudit = createSelector(
  getFlightCrewMemberAuditState,
  auditLogSelectors.selectAll
);

// flight business person audit
export const getFlightBusinessPersonAuditLoading = createSelector(
  getFlightBusinessPersonAuditState,
  state => state.loading
);
export const getFlightBusinessPersonAuditLoaded = createSelector(
  getFlightBusinessPersonAuditState,
  state => state.loaded
);
export const getFlightBusinessPersonAudit = createSelector(
  getFlightBusinessPersonAuditState,
  auditLogSelectors.selectAll
);

// log book approach audit
export const getLogbookApproachAuditLoading = createSelector(
  getLogbookApproachAuditState,
  state => state.loading
);
export const getLogbookApproachAuditLoaded = createSelector(
  getLogbookApproachAuditState,
  state => state.loaded
);
export const getLogbookApproachAudit = createSelector(
  getLogbookApproachAuditState,
  auditLogSelectors.selectAll
);

// flight passenger audit
export const getFlightPassengerAuditLoading = createSelector(
  getFlightPassengerAuditState,
  state => state.loading
);
export const getFlightPassengerAuditLoaded = createSelector(
  getFlightPassengerAuditState,
  state => state.loaded
);
export const getFlightPassengerAudit = createSelector(
  getFlightPassengerAuditState,
  auditLogSelectors.selectAll
);

// expense audit
export const getExpenseAuditLoading = createSelector(getExpenseAuditState, state => state.loading);
export const getExpenseAuditLoaded = createSelector(getExpenseAuditState, state => state.loaded);
export const getExpenseAudit = createSelector(getExpenseAuditState, auditLogSelectors.selectAll);

// posted flight engine audit
export const getPostedFlightEngineAuditLoading = createSelector(
  getPostedFlightEngineAuditState,
  state => state.loading
);
export const getPostedFlightEngineAuditLoaded = createSelector(
  getPostedFlightEngineAuditState,
  state => state.loaded
);
export const getPostedFlightEngineAudit = createSelector(
  getPostedFlightEngineAuditState,
  auditLogSelectors.selectAll
);

// posted flight component audit
export const getPostedFlightComponentAuditLoading = createSelector(
  getPostedFlightComponentAuditState,
  state => state.loading
);
export const getPostedFlightComponentAuditLoaded = createSelector(
  getPostedFlightComponentAuditState,
  state => state.loaded
);
export const getPostedFlightComponentAudit = createSelector(
  getPostedFlightComponentAuditState,
  auditLogSelectors.selectAll
);

// posted flight fuel audit
export const getPostedFlightFuelAuditLoading = createSelector(
  getPostedFlightFuelAuditState,
  state => state.loading
);
export const getPostedFlightFuelAuditLoaded = createSelector(
  getPostedFlightFuelAuditState,
  state => state.loaded
);
export const getPostedFlightFuelAudit = createSelector(
  getPostedFlightFuelAuditState,
  auditLogSelectors.selectAll
);

// posted flight additional detail audit
export const getPostedFlightAdditionalDetailAuditLoading = createSelector(
  getPostedFlightAdditionalDetailAuditState,
  state => state.loading
);
export const getPostedFlightAdditionalDetailAuditLoaded = createSelector(
  getPostedFlightAdditionalDetailAuditState,
  state => state.loaded
);
export const getPostedFlightAdditionalDetailAudit = createSelector(
  getPostedFlightAdditionalDetailAuditState,
  auditLogSelectors.selectAll
);

// oil fluid audit
export const getOilFluidAuditLoading = createSelector(
  getOilFluidAuditState,
  state => state.loading
);
export const getOilFluidAuditLoaded = createSelector(getOilFluidAuditState, state => state.loaded);
export const getOilFluidAudit = createSelector(getOilFluidAuditState, auditLogSelectors.selectAll);

// engine oil fluid audit
export const getEngineOilFluidAuditLoading = createSelector(
  getEngineOilFluidAuditState,
  state => state.loading
);
export const getEngineOilFluidAuditLoaded = createSelector(
  getEngineOilFluidAuditState,
  state => state.loaded
);
export const getEngineOilFluidAudit = createSelector(
  getEngineOilFluidAuditState,
  auditLogSelectors.selectAll
);

// component oil fluid audit
export const getComponentOilFluidAuditLoading = createSelector(
  getComponentOilFluidAuditState,
  state => state.loading
);
export const getComponentOilFluidAuditLoaded = createSelector(
  getComponentOilFluidAuditState,
  state => state.loaded
);
export const getComponentOilFluidAudit = createSelector(
  getComponentOilFluidAuditState,
  auditLogSelectors.selectAll
);

// posted flight de/anti ice audit
export const getPostedFlightDeAntiIceAuditLoading = createSelector(
  getPostedFlightDeAntiIceAuditState,
  state => state.loading
);
export const getPostedFlightDeAntiIceAuditLoaded = createSelector(
  getPostedFlightDeAntiIceAuditState,
  state => state.loaded
);
export const getPostedFlightDeAntiIceAudit = createSelector(
  getPostedFlightDeAntiIceAuditState,
  auditLogSelectors.selectAll
);

// posted flight notes audit
export const getPostedFlightNotesAuditLoading = createSelector(
  getPostedFlightNotesAuditState,
  state => state.loading
);
export const getPostedFlightNotesAuditLoaded = createSelector(
  getPostedFlightNotesAuditState,
  state => state.loaded
);
export const getPostedFlightNotesAudit = createSelector(
  getPostedFlightNotesAuditState,
  auditLogSelectors.selectAll
);

// fuel uplift audit
export const getFuelUpliftAuditLoading = createSelector(
  getFuelUpliftAuditState,
  state => state.loading
);
export const getFuelUpliftAuditLoaded = createSelector(
  getFuelUpliftAuditState,
  state => state.loaded
);
export const getFuelUpliftAudit = createSelector(
  getFuelUpliftAuditState,
  auditLogSelectors.selectAll
);

// business category audit
export const getBusinessCategoryAuditLoading = createSelector(
  getBusinessCategoryAuditState,
  state => state.loading
);
export const getBusinessCategoryAuditLoaded = createSelector(
  getBusinessCategoryAuditState,
  state => state.loaded
);
export const getBusinessCategoryAudit = createSelector(
  getBusinessCategoryAuditState,
  auditLogSelectors.selectAll
);

// department audit
export const getDepartmentAuditLoading = createSelector(
  getDepartmentAuditState,
  state => state.loading
);
export const getDepartmentAuditLoaded = createSelector(
  getDepartmentAuditState,
  state => state.loaded
);
export const getDepartmentAudit = createSelector(
  getDepartmentAuditState,
  auditLogSelectors.selectAll
);

// logbook audit
export const getLogbookAuditLoading = createSelector(getLogbookAuditState, state => state.loading);
export const getLogbookAuditLoaded = createSelector(getLogbookAuditState, state => state.loaded);
export const getLogbookAudit = createSelector(getLogbookAuditState, auditLogSelectors.selectAll);

// crew member duty audit
export const getCrewMemberDutyAuditLoading = createSelector(
  getCrewMemberDutyAuditState,
  state => state.loading
);
export const getCrewMemberDutyAuditLoaded = createSelector(
  getCrewMemberDutyAuditState,
  state => state.loaded
);
export const getCrewMemberDutyAudit = createSelector(
  getCrewMemberDutyAuditState,
  auditLogSelectors.selectAll
);

//Airframe/Engine
export const getAirFrameLandingsAuditLoadingSelector = createSelector(
  getAirFrameLandingsAuditState,
  state => state.loading
);
export const getAirFrameLandingsAuditLoadedSelector = createSelector(
  getAirFrameLandingsAuditState,
  state => state.loaded
);
export const getAirFrameLandingsAuditSelector = createSelector(
  getAirFrameLandingsAuditState,
  auditLogSelectors.selectAll
);

// engine cycles audit
export const getEngineCyclesAuditLoadingSelector = createSelector(
  getEngineCyclesAuditState,
  state => state.loading
);
export const getEngineCyclesAuditLoadedSelector = createSelector(
  getEngineCyclesAuditState,
  state => state.loaded
);
export const getEngineCyclesAuditSelector = createSelector(
  getEngineCyclesAuditState,
  auditLogSelectors.selectAll
);
//VOR Check Data audit
export const getVORCheckDataAuditLoadingSelector = createSelector(
  getVORCheckDataAuditState,
  state => state.loading
);
export const getVORCheckDataAuditLoadedSelector = createSelector(
  getVORCheckDataAuditState,
  state => state.loaded
);
export const getVORCheckDataAuditSelector = createSelector(
  getVORCheckDataAuditState,
  auditLogSelectors.selectAll
);

// pre-flight user eSignatures audit
export const getPreFlightChecklistUserESignaturesAuditLoading = createSelector(
  getPreFlightChecklistUserESignaturesAuditState,
  state => state.loading
);
export const getPreFlightChecklistUserESignaturesAuditLoaded = createSelector(
  getPreFlightChecklistUserESignaturesAuditState,
  state => state.loaded
);
export const getPreFlightChecklistUserESignaturesAudit = createSelector(
  getPreFlightChecklistUserESignaturesAuditState,
  auditLogSelectors.selectAll
);

// posted flight user eSignatures audit
export const getPostedFlightChecklistUserESignaturesAuditLoading = createSelector(
  getPostedFlightChecklistUserESignaturesAuditState,
  state => state.loading
);
export const getPostedFlightChecklistUserESignaturesAuditLoaded = createSelector(
  getPostedFlightChecklistUserESignaturesAuditState,
  state => state.loaded
);
export const getPostedFlightChecklistUserESignaturesAudit = createSelector(
  getPostedFlightChecklistUserESignaturesAuditState,
  auditLogSelectors.selectAll
);

// flight log user eSignatures audit
export const getFlightLogUserESignaturesAuditLoading = createSelector(
  getFlightLogUserESignaturesAuditState,
  state => state.loading
);
export const getFlightLogUserESignaturesAuditLoaded = createSelector(
  getFlightLogUserESignaturesAuditState,
  state => state.loaded
);
export const getFlightLogUserESignaturesAudit = createSelector(
  getFlightLogUserESignaturesAuditState,
  auditLogSelectors.selectAll
);

// squawk user eSignatures audit
export const getSquawkUserESignaturesAuditLoading = createSelector(
  getSquawkUserESignaturesAuditState,
  state => state.loading
);
export const getSquawkUserESignaturesAuditLoaded = createSelector(
  getSquawkUserESignaturesAuditState,
  state => state.loaded
);
export const getSquawkUserESignaturesAudit = createSelector(
  getSquawkUserESignaturesAuditState,
  auditLogSelectors.selectAll
);
